import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Button,
  Paper,
} from "@mui/material";
import {
  Close,
  DownloadForOffline,
  Link as LinkIcon,
  PictureAsPdf,
  Science,
  Assignment,
  DateRange, TableChart
} from "@mui/icons-material";
import Sample from "./Sample";
import DownloadAnchor from "../common/DownloadAnchor";
import OrderProgressBar from "./OrderProgressbar";
import { getOrder } from "../../services/orderService";
import { getCoc, getLabReport } from "../../services/reportService";


const ToDate = (input) => {
  if (input) {
    let date = new Date(input);
    let output = date.toLocaleDateString();
    //handle default date from API
    if (date.getYear() === 1) return "";
    return output;
  } else {
    return "";
  }
};

export default function Order() {
  const { orderNumber } = useParams();
  const [percentComplete, setPercentComplete] = useState(0);
  const [order, setOrder] = useState();
  const hasDownloads = useRef(false);
  const hasReport = useRef(false);

  useEffect(() => {
    async function GetOrder() {
      let data = await getOrder(orderNumber);

      hasReport.current =
        data.Status === "Analysis Complete" ||
        data.Status === "Reported" ||
        data.Status === "Invoiced" ||
        data.Status === "Paid";

      if (data.Cocs === true || hasReport) hasDownloads.current = true;

      setOrder(data);

      setTimeout(() => {
        switch (data.Status) {
          case "Analysis In Progress":
            setPercentComplete(25);
            break;
          case "Reported":
            setPercentComplete(50);
            break;
          case "Invoiced":
            setPercentComplete(75);
            break;
          default:
            setPercentComplete(100);
        }
      }, 1500);
    }

    GetOrder();
  }, [orderNumber]);


  const [anchorEl, setAnchorEl] = useState(null);
  const downloadsOpen = Boolean(anchorEl);

  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: "flex", mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: "160px", color: "#555" }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ ml: 1, color: "#333" }}>
        {value || "N/A"}
      </Typography>
    </Box>
  );

  return (
    <>
      {order && (
        <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>

          {/* Header */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
                  Order # {orderNumber}
                </Typography>
                {order.Status && (
                  <Chip
                    label={order.Status}
                    size="small"
                    color={
                      order.Status === "Analysis Complete" || order.Status === "Paid"
                        ? "success"
                        : order.Status === "Invoiced"
                          ? "warning"
                          : "info"
                    }
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {hasDownloads.current && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadForOffline />}
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      sx={{ textTransform: "none" }}
                    >
                      Downloads
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={downloadsOpen}
                      onClose={() => setAnchorEl(null)}
                      PaperProps={{ elevation: 2, sx: { minWidth: 220 } }}
                    >
                      {order.Cocs?.map((s, i) => (
                        <MenuItem key={i} onClick={() => setAnchorEl(null)} sx={{ gap: 1 }}>
                          <LinkIcon fontSize="small" color="primary" />
                          <DownloadAnchor
                            func={() => getCoc(s)}
                            hoverText="Chain of Custody"
                            text="Chain of Custody"
                          />
                        </MenuItem>
                      ))}

                      {order.Status !== "Analysis In Progress" && (
                        <>
                          <Divider sx={{ my: 0.5 }} />

                          <MenuItem onClick={() => setAnchorEl(null)} sx={{ gap: 1 }}>
                            <PictureAsPdf fontSize="small" color="error" />
                            <DownloadAnchor
                              hoverText="Order Results (PDF)"
                              func={() => getLabReport(orderNumber, "PDF")}
                              text="Order Results (PDF)"
                            />
                          </MenuItem>

                          <MenuItem onClick={() => setAnchorEl(null)} sx={{ gap: 1 }}>
                            <TableChart fontSize="small" sx={{ color: "#217346" }} />
                            <DownloadAnchor
                              hoverText="Order Results (CSV)"
                              func={() => getLabReport(orderNumber, "CSV")}
                              text="Order Results (CSV)"
                            />
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </>
                )}
                <Tooltip title="Close">
                  <IconButton component={Link} to="/" size="small">
                    <Close />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <OrderProgressBar percentComplete={percentComplete} />
          </Paper>

          {/* Project Info */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Assignment sx={{ color: "#1976d2", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Project Info
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label="Project Description" value={order.ProjectCategory} />
                  <InfoRow label="Order Type" value={order.ProjectCategory} />
                  <InfoRow label="PWS Number" value={order.PwsNumber} />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <DateRange sx={{ color: "#1976d2", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Dates & Invoice
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: "flex", mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: "160px", color: "#555" }}>
                      Invoice:
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {order.InvoiceNumber ? (
                        <Link to={`/invoice/${order.InvoiceNumber}`} style={{ color: "#1976d2" }}>
                          {order.InvoiceNumber}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </Typography>
                  </Box>
                  <InfoRow label="Date Received" value={ToDate(order.DateReceived)} />
                  <InfoRow label="Date Reported" value={ToDate(order.DateReported)} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Science sx={{ color: "#1976d2", mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Samples
                </Typography>
                {order.Samples?.length > 0 && (
                  <Chip label={order.Samples.length} size="small" color="primary" sx={{ ml: 1 }} />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {order.Samples?.map((s) => (
                  <Grid item xs={12} sm={6} md={4} key={s.LabId}>
                    <Sample sample={s} />
                  </Grid>
                ))}
              </Grid>

            </CardContent>
          </Card>

        </Box>
      )}
    </>
  );
}
